import { Osdk } from "@osdk/client";
import { Analysis } from "@unruly-osdk/sdk";
import { useServices } from "../services/Services";
import { useDeepDiveToast } from "../context/DeepDiveToastContext";
import { useDeepDiveTracking } from "../context/DeepDiveTrackingContext";
import { getDomainFromUrl } from "../helper/helper";

export type DeepDiveRequestStatus =
  "idle" | "requested" | "processing" | "completed" | "failed";

interface UseEventDeepDiveOptions {
  eventId: string;
  eventName?: string;
  sourceUrl?: string;
  userId?: string;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
}

export function useEventDeepDive({
  eventId,
  eventName,
  sourceUrl,
  userId,
  onOpenAnalysis,
}: UseEventDeepDiveOptions) {
  const services = useServices();
  const { showEventDeepDiveToast } = useDeepDiveToast();
  const {
    isEventDeepDiveCompleted,
    isEventDeepDiveFailed,
    getEventDeepDiveTracking,
  } = useDeepDiveTracking();

  const createRequestMutation =
    services.Request.useCreateEventDeepDiveRequest();

  const { data: existingRequest } = services.Request.useGetEventDeepDiveRequest(
    eventId,
    userId || "",
  );

  const { data: analysis } = services.Analysis.useGetAnalysisByRequestId(
    existingRequest?.id || "",
  );

  const deepDiveTracking = getEventDeepDiveTracking(eventId);

  const isCompleted =
    existingRequest?.status === "Done" || isEventDeepDiveCompleted(eventId);
  const isFailed =
    existingRequest?.status === "Failed" || isEventDeepDiveFailed(eventId);

  const status: DeepDiveRequestStatus = isFailed
    ? "failed"
    : isCompleted
      ? "completed"
      : existingRequest?.status === "New"
        ? "requested"
        : existingRequest?.status === "Pending"
          ? "processing"
          : existingRequest
            ? "processing"
            : "idle";

  const handleRequestDeepDive = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!userId) return;
    if (existingRequest && existingRequest.status !== "Failed") return;

    try {
      await createRequestMutation.mutateAsync({ eventId, userId });
      const sourceDomain = sourceUrl ? getDomainFromUrl(sourceUrl) || "" : "";
      showEventDeepDiveToast(eventId, eventName, sourceDomain);
    } catch (error) {
      console.error("Failed to create deep dive request:", error);
    }
  };

  const handleOpenDeepDive = () => {
    if (analysis && onOpenAnalysis) {
      const analysisEventId =
        analysis.name?.split("|")[1]?.split(":")[1]?.trim() || eventId;
      onOpenAnalysis(analysis, analysisEventId);
    } else if (
      deepDiveTracking?.status === "done" &&
      deepDiveTracking.analysisId &&
      onOpenAnalysis
    ) {
      const trackedAnalysis = {
        id: deepDiveTracking.analysisId,
      } as Osdk.Instance<Analysis>;
      onOpenAnalysis(trackedAnalysis, eventId);
    }
  };

  return {
    status,
    isLoading: createRequestMutation.isPending,
    analysis,
    existingRequest,
    handleRequestDeepDive,
    handleOpenDeepDive,
  };
}
